import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useNavigate } from 'react-router-dom';
import './App.css';
import './styles/MobileResponsive.css';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ROLES, ROLE_DASHBOARD_MAP } from './constants/roles';
import { useHistoryManagement } from './hooks/useHistoryManagement';
import { UserIcon, LogoutIcon, UsersIcon, DiamondIcon, PhoneIcon, UserPlusIcon } from './components/Icons';
import AuthPage from './components/Auth';
import Dashboard from './components/Dashboard';
import ProfilePage from './components/Profile';
import ClubsPage from './components/Clubs';
import ClubTemplate from './components/ClubTemplate';
import EventsPage from './components/Events';
import EventsPageNew from './components/EventsPage';
import EventTemplate from './components/EventTemplate';
import CreateEvent from './components/CreateEvent';
import EventEditPage from './components/EventEditPage';
import SuperAdminDashboard from './components/SuperAdminDashboard';
import AcceptAdminInvite from './components/AcceptAdminInvite';
import AdminInvitePortal from './components/AdminInvitePortal';
import ProtectedRouteWithRole from './components/ProtectedRoute';
// Sidebar removed — navigation moved to profile dropdown
import Contact from './components/Contact';
import AboutUs from './components/AboutUs';
import Sponsors from './components/Sponsors';
import Landing from './components/Landing';
import Nexaprom from './components/Nexaprom';
import PromotionReview from './components/PromotionReview';
import PromotionNotificationIcon from './components/PromotionNotificationIcon';
import ClubAdminDashboard from './components/ClubAdminDashboard';
import img4 from './assets/image.png';
import defaultAvatar from './assets/avatar1.png';

// Enhanced Navigation Bar Component for all dashboards
const DashboardNavBar: React.FC<{
  logoLink: string;
  navLinks: { label: string; path: string }[];
}> = ({ logoLink, navLinks }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/', { replace: true });
    setShowProfileDropdown(false);
  };

  const handleProfileClick = (userRole: string) => {
    const normalizedRole = userRole.toUpperCase().replace('-', '_');
    if (normalizedRole === 'STUDENT') {
      navigate('/dashboard/student/profile');
    } else if (normalizedRole === 'CLUB_ADMIN') {
      navigate('/dashboard/club/profile');
    } else if (normalizedRole === 'SUPER_ADMIN') {
      navigate('/dashboard/admin/profile');
    }
    setShowProfileDropdown(false);
  };

  return (
    <div className="top-navbar">
      <Link to={logoLink} className="navbar-logo">
        NEXUS
      </Link>
      <div className="nav-center">
        {navLinks.map((link) => (
          <Link key={link.path} to={link.path} className="nav-link">
            {link.label}
          </Link>
        ))}
      </div>

      {/* Profile Section - Authentication Aware */}
      <div
        className="profile-dropdown-trigger"
        onMouseEnter={() => setShowProfileDropdown(true)}
        onMouseLeave={() => setShowProfileDropdown(false)}
        style={{ position: 'relative' }}
      >
        {user ? (
          <>
            <img
              src={user.avatar || defaultAvatar}
              alt="Profile"
              className="nav-profile-avatar"
              style={{ cursor: 'pointer' }}
            />
            {showProfileDropdown && (
              <div
                className="profile-dropdown-menu"
                style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  backgroundColor: 'rgba(0, 20, 40, 0.95)',
                  border: '1px solid #00ffff',
                  borderRadius: '8px',
                  minWidth: '200px',
                  zIndex: 1000,
                  boxShadow: '0 8px 32px rgba(0, 255, 255, 0.2)',
                }}
              >
                <div
                  style={{
                    padding: '12px 16px',
                    borderBottom: '1px solid rgba(0, 255, 255, 0.3)',
                  }}
                >
                  <p style={{ color: '#00ffff', margin: '4px 0', fontWeight: 'bold' }}>
                    {user.username}
                  </p>
                  <p style={{ color: '#00ffff', margin: '4px 0', fontSize: '0.85rem', opacity: 0.7 }}>
                    {user.email}
                  </p>
                </div>
                <button
                  onClick={() => handleProfileClick(user.role)}
                  style={{
                    width: '100%',
                    padding: '10px 16px',
                    background: 'none',
                    border: 'none',
                    color: '#00ffff',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontSize: '0.9rem',
                    transition: 'background-color 0.2s',
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = 'rgba(0, 255, 255, 0.1)')
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = 'transparent')
                  }
                >
                  <UserIcon size={14} /> View Profile
                </button>
                <button
                  onClick={handleLogout}
                  style={{
                    width: '100%',
                    padding: '10px 16px',
                    background: 'none',
                    border: 'none',
                    color: '#ff006e',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontSize: '0.9rem',
                    transition: 'background-color 0.2s',
                    borderTop: '1px solid rgba(0, 255, 255, 0.3)',
                  }}
                  onMouseEnter={(e) =>
                    (e.currentTarget.style.backgroundColor = 'rgba(255, 0, 110, 0.1)')
                  }
                  onMouseLeave={(e) =>
                    (e.currentTarget.style.backgroundColor = 'transparent')
                  }
                >
                  <LogoutIcon size={14} color="#ff006e" /> Logout
                </button>
              </div>
            )}
          </>
        ) : (
          <>
            <button
              onClick={() => navigate('/auth')}
              className="nav-link auth-btn"
              style={{ background: 'none', border: 'none', cursor: 'pointer' }}
            >
              SIGN IN / SIGN UP
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// Student Dashboard Layout
const StudentDashboardLayout: React.FC = () => {
  const [showEventsDropdown, setShowEventsDropdown] = useState(false);
  const eventCategories = ['WORKSHOPS', 'EXHIBITIONS', 'SEMINARS'];

  return (
    <div className="dashboard-layout">
      <img
        src={img4}
        alt="background"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center',
          filter: 'brightness(0.5)',
          opacity: 0.6,
          zIndex: 0,
          pointerEvents: 'none',
        }}
      />

      <div className="top-navbar">
        <div className="navbar-left-cluster">
          <Link to="/dashboard/student" className="navbar-logo">
            NEXUS
          </Link>
        </div>
        <div className="nav-center">
          <Link to="/dashboard/student/clubs" className="nav-link">
            CLUBS
          </Link>
          <div 
            className="nav-link-dropdown"
            onMouseEnter={() => setShowEventsDropdown(true)}
            onMouseLeave={() => setShowEventsDropdown(false)}
          >
            <Link to="/dashboard/student/events" className="nav-link">
              EVENTS
            </Link>
            {showEventsDropdown && (
              <div className="dropdown-menu">
                {eventCategories.map((category) => (
                  <Link 
                    key={category}
                    to={`/dashboard/student/events?category=${category.toLowerCase()}`}
                    className="dropdown-item"
                  >
                    {category}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Profile Avatar with Dropdown */}
        <StudentProfileNavigation />
      </div>

      <main className="dashboard-main" style={{ marginLeft: '0px' }}>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/clubs" element={<ClubsPage />} />
          <Route path="/clubs/:id" element={<ClubTemplate />} />
          <Route path="/events" element={<EventsPage />} />
          <Route path="/events/:eventId/edit" element={<EventEditPage />} />
          <Route path="/events/:eventId" element={<EventTemplate />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/aboutus" element={<AboutUs />} />
          <Route path="/sponsors" element={<Sponsors />} />
        </Routes>
      </main>
    </div>
  );
};

// Student Profile Navigation Component
const StudentProfileNavigation: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  const handleLogout = () => {
    logout();
    navigate('/', { replace: true });
    setShowDropdown(false);
  };

  const getBasePath = () => {
    if (user?.role) {
      const normalizedRole = user.role.toUpperCase().replace('-', '_');
      return ROLE_DASHBOARD_MAP[normalizedRole as keyof typeof ROLE_DASHBOARD_MAP] || '/dashboard/student';
    }
    return '/dashboard/student';
  };

  const handleProfileClick = () => {
    navigate(`${getBasePath()}/profile`);
    setShowDropdown(false);
  };

  const handleNavClick = (path: string) => {
    navigate(`${getBasePath()}/${path}`);
    setShowDropdown(false);
  };

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };
    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDropdown]);

  const dropdownItemStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 16px',
    background: 'none',
    border: 'none',
    color: '#00ffff',
    cursor: 'pointer',
    textAlign: 'left',
    fontSize: '0.9rem',
    fontFamily: "'Rajdhani', sans-serif",
    fontWeight: 600,
    letterSpacing: '0.5px',
    transition: 'background-color 0.2s',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  };

  const isSuperAdmin = user?.role === 'SUPER_ADMIN' || user?.role === 'super_admin';

  return (
    <div
      ref={dropdownRef}
      className="profile-dropdown-trigger"
      style={{ position: 'relative', zIndex: 10001 }}
    >
      <img
        src={user?.avatar || defaultAvatar}
        alt="Profile"
        className="nav-profile-avatar"
        onClick={toggleDropdown}
        style={{ cursor: 'pointer' }}
        title="Profile Menu"
      />
      {showDropdown && (
        <div
          className="profile-dropdown-menu cyber-dropdown-panel"
          style={{
            position: 'absolute',
            top: 'calc(100% + 10px)',
            right: 0,
            backgroundColor: 'rgba(8, 18, 40, 0.97)',
            border: '1.5px solid rgba(0, 255, 255, 0.6)',
            borderRadius: '10px',
            minWidth: '230px',
            zIndex: 10002,
            boxShadow: '0 8px 32px rgba(0, 255, 255, 0.4), 0 0 50px rgba(0, 255, 255, 0.15)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            animation: 'dropdownFadeIn 0.2s ease-out',
            overflow: 'hidden',
          }}
        >
          {/* User info header */}
          <div style={{ padding: '14px 16px', borderBottom: '1px solid rgba(0, 255, 255, 0.2)' }}>
            <p style={{ color: '#ffffff', margin: '2px 0', fontWeight: 'bold', fontSize: '0.95rem' }}>
              {user?.username}
            </p>
            <p style={{ color: 'rgba(0, 255, 255, 0.7)', margin: '2px 0', fontSize: '0.8rem' }}>
              {user?.email}
            </p>
          </div>

          {/* Navigation items */}
          <button onClick={handleProfileClick} style={dropdownItemStyle}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(0, 255, 255, 0.1)')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            <UserIcon size={15} /> Profile Settings
          </button>
          <button onClick={() => handleNavClick('aboutus')} style={dropdownItemStyle}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(0, 255, 255, 0.1)')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            <UsersIcon size={15} /> About Us
          </button>
          <button onClick={() => handleNavClick('sponsors')} style={dropdownItemStyle}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(0, 255, 255, 0.1)')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            <DiamondIcon size={15} /> Sponsors
          </button>
          <button onClick={() => handleNavClick('contact')} style={dropdownItemStyle}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(0, 255, 255, 0.1)')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
          >
            <PhoneIcon size={15} /> Contact
          </button>

          {/* Invite Admin - Only visible for Super Admins */}
          {isSuperAdmin && (
            <button onClick={() => handleNavClick('invite-admins')} style={dropdownItemStyle}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(0, 255, 255, 0.1)')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              <UserPlusIcon size={15} /> Invite Admin
            </button>
          )}

          {/* Divider + Sign Out */}
          <div style={{ borderTop: '1px solid rgba(255, 0, 110, 0.3)', marginTop: '4px' }}>
            <button
              onClick={handleLogout}
              style={{ ...dropdownItemStyle, color: '#ff006e', padding: '12px 16px' }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'rgba(255, 0, 110, 0.1)')}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              <LogoutIcon size={15} color="#ff006e" /> Sign Out
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Club Admin Dashboard Layout
const ClubAdminDashboardLayout: React.FC = () => {
  return (
    <div className="dashboard-layout">
      <img
        src={img4}
        alt="background"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center',
          filter: 'brightness(0.5)',
          opacity: 0.6,
          zIndex: 0,
          pointerEvents: 'none',
        }}
      />

      <div className="top-navbar">
        <div className="navbar-left-cluster">
          <Link to="/dashboard/club" className="navbar-logo">
            NEXUS
          </Link>
        </div>
        <div className="nav-center">
          <Link to="/dashboard/club/clubs" className="nav-link">
            CLUBS
          </Link>
          <Link to="/dashboard/club/events" className="nav-link">
            EVENTS
          </Link>
        </div>

        {/* Profile Avatar with Dropdown */}
        <div className="student-profile-nav">
          <StudentProfileNavigation />
        </div>
      </div>

      <main className="dashboard-main" style={{ marginLeft: '0px' }}>
        <Routes>
          <Route path="/" element={<ClubAdminDashboard />} />
          <Route path="/dashboard" element={<ClubAdminDashboard />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/clubs" element={<ClubsPage />} />
          <Route path="/clubs/:id" element={<ClubTemplate />} />
          <Route path="/events" element={<EventsPage />} />
          <Route path="/events/:eventId/edit" element={<EventEditPage />} />
          <Route path="/events/:eventId" element={<EventTemplate />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/aboutus" element={<AboutUs />} />
          <Route path="/sponsors" element={<Sponsors />} />
        </Routes>
      </main>
    </div>
  );
};

// Super Admin Dashboard Layout Component
const AuthRoute: React.FC = () => {
  const { user } = useAuth();
  
  // If user is already logged in, redirect to their dashboard
  if (user) {
    const normalizedRole = (user.role.toUpperCase().replace('-', '_'));
    console.log('AuthRoute - Already logged in as:', normalizedRole, '- Redirecting to dashboard');
    if (normalizedRole === 'STUDENT') return <Navigate to="/" replace />;
    if (normalizedRole === 'CLUB_ADMIN') return <Navigate to="/dashboard/club" replace />;
    if (normalizedRole === 'SUPER_ADMIN') return <Navigate to="/dashboard/admin" replace />;
  }
  
  // Not logged in - show auth page
  console.log('AuthRoute - Not logged in - showing auth page');
  return <AuthPage />;
};

// Public Dashboard Layout - for both authenticated and guest users
const PublicDashboardLayout: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [showEventsDropdown, setShowEventsDropdown] = useState(false);

  const eventCategories = ['WORKSHOPS', 'EXHIBITIONS', 'SEMINARS'];

  // Redirect authenticated users to their role-specific dashboard when accessing root
  React.useEffect(() => {
    if (user && window.location.pathname === '/') {
      const normalizedRole = user.role.toUpperCase().replace('-', '_');
      const dashboardPath = ROLE_DASHBOARD_MAP[normalizedRole as keyof typeof ROLE_DASHBOARD_MAP];
      if (dashboardPath && dashboardPath !== '/') {
        console.log('Redirecting authenticated user to:', dashboardPath);
        navigate(dashboardPath, { replace: true });
        // Clear the justLoggedIn flag after redirect
        localStorage.removeItem('justLoggedIn');
      }
    }
  }, [user, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/', { replace: true });
    setShowProfileDropdown(false);
  };

  const handleSignIn = () => {
    navigate('/auth');
  };

  return (
    <div className="dashboard-layout">
      <img
        src={img4}
        alt="background"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center',
          filter: 'brightness(0.5)',
          opacity: 0.6,
          zIndex: 0,
          pointerEvents: 'none',
        }}
      />

      <div className="top-navbar">
        <div className="navbar-left-cluster">
          <Link to="/public/" className="navbar-logo">
            NEXUS
          </Link>
        </div>
        <div className="nav-center">
          <Link to="/public/clubs" className="nav-link">
            CLUBS
          </Link>
          <div 
            className="nav-link-dropdown"
            onMouseEnter={() => setShowEventsDropdown(true)}
            onMouseLeave={() => setShowEventsDropdown(false)}
          >
            <Link to="/public/events" className="nav-link">
              EVENTS
            </Link>
            {showEventsDropdown && (
              <div className="dropdown-menu">
                {eventCategories.map((category) => (
                  <Link 
                    key={category}
                    to={`/public/events?category=${category.toLowerCase()}`}
                    className="dropdown-item"
                  >
                    {category}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Sign In / Profile Section */}
        <div className="student-profile-nav"
          style={{ position: 'relative' }}
        >
          {user ? (
            <>
              <img
                src={user.avatar || defaultAvatar}
                alt="Profile"
                className="nav-profile-avatar"
                style={{ cursor: 'pointer' }}
              />
              {showProfileDropdown && (
                <div
                  className="profile-dropdown-menu"
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    backgroundColor: 'rgba(0, 20, 40, 0.95)',
                    border: '1px solid #00ffff',
                    borderRadius: '8px',
                    minWidth: '200px',
                    zIndex: 1000,
                    boxShadow: '0 8px 32px rgba(0, 255, 255, 0.2)',
                  }}
                >
                  <div
                    style={{
                      padding: '12px 16px',
                      borderBottom: '1px solid rgba(0, 255, 255, 0.3)',
                    }}
                  >
                    <p style={{ color: '#00ffff', margin: '4px 0', fontWeight: 'bold' }}>
                      {user.username}
                    </p>
                    <p style={{ color: '#00ffff', margin: '4px 0', fontSize: '0.85rem', opacity: 0.7 }}>
                      {user.email}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      navigate('/dashboard/profile');
                      setShowProfileDropdown(false);
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 16px',
                      background: 'none',
                      border: 'none',
                      color: '#00ffff',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontSize: '0.9rem',
                      transition: 'background-color 0.2s',
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor = 'rgba(0, 255, 255, 0.1)')
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = 'transparent')
                    }
                  >
                    <UserIcon size={14} /> View Profile
                  </button>
                  <button
                    onClick={handleLogout}
                    style={{
                      width: '100%',
                      padding: '10px 16px',
                      background: 'none',
                      border: 'none',
                      color: '#ff006e',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontSize: '0.9rem',
                      transition: 'background-color 0.2s',
                      borderTop: '1px solid rgba(0, 255, 255, 0.3)',
                    }}
                    onMouseEnter={(e) =>
                      (e.currentTarget.style.backgroundColor = 'rgba(255, 0, 110, 0.1)')
                    }
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor = 'transparent')
                    }
                  >
                    <LogoutIcon size={14} color="#ff006e" /> Logout
                  </button>
                </div>
              )}
            </>
          ) : (
            <button
              onClick={handleSignIn}
              style={{
                backgroundColor: '#7c3aed',
                color: 'white',
                border: 'none',
                padding: '10px 24px',
                borderRadius: '6px',
                fontSize: '0.9rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                fontFamily: 'Rajdhani, monospace',
                transition: 'all 0.3s ease',
                boxShadow: '0 0 20px rgba(124, 58, 237, 0.5)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#a855f7';
                e.currentTarget.style.boxShadow = '0 0 30px rgba(124, 58, 237, 0.8)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#7c3aed';
                e.currentTarget.style.boxShadow = '0 0 20px rgba(124, 58, 237, 0.5)';
              }}
            >
              SIGN IN
            </button>
          )}
        </div>
      </div>

      <main className="dashboard-main" style={{ marginLeft: '0px' }}>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/dashboard" element={<Dashboard />} />
          {/* Protected routes - require authentication */}
          <Route
            path="/clubs/:id"
            element={<ClubTemplate />}
          />
          <Route
            path="/events/register/:id"
            element={
              <ProtectedRouteWithRole>
                <EventsPage />
              </ProtectedRouteWithRole>
            }
          />
          {/* Event Management Routes */}
          <Route path="/events-browse" element={<EventsPageNew />} />
          <Route path="/events/:eventId" element={<EventTemplate />} />
          {/* Non-protected routes */}
          <Route path="/clubs" element={<ClubsPage />} />
          <Route path="/events" element={<EventsPage />} />
          {/* User profile - protected */}
          <Route
            path="/profile"
            element={
              <ProtectedRouteWithRole>
                <ProfilePage />
              </ProtectedRouteWithRole>
            }
          />
          {/* Public dashboard pages */}
          <Route path="/contact" element={<Contact />} />
          <Route path="/aboutus" element={<AboutUs />} />
          <Route path="/sponsors" element={<Sponsors />} />
        </Routes>
      </main>
    </div>
  );
};

// Main Router Component
const AppRoutes: React.FC = () => {
  // Initialize history management to prevent back navigation to auth pages
  useHistoryManagement();

  return (
    <Routes>
      {/* Landing Page at root - accessible to everyone */}
      <Route path="/" element={<Landing />} />
      <Route path="/landing" element={<Landing />} />

      {/* Nexaprom Page - accessible to everyone */}
      <Route path="/nexaprom" element={<Nexaprom />} />

      {/* Auth page */}
      <Route path="/auth" element={<AuthRoute />} />

      {/* Admin Invite Acceptance */}
      <Route path="/setup-admin" element={<AcceptAdminInvite />} />

      {/* Public Dashboard - accessible to everyone */}
      <Route path="/public/*" element={<PublicDashboardLayout />} />

      {/* Student Dashboard - Protected by Role */}
      <Route
        path="/dashboard/student/*"
        element={
          <ProtectedRouteWithRole requiredRole={ROLES.STUDENT}>
            <StudentDashboardLayout />
          </ProtectedRouteWithRole>
        }
      />

      {/* Club Admin Dashboard - Protected by Role */}
      <Route
        path="/dashboard/club/*"
        element={
          <ProtectedRouteWithRole requiredRole={ROLES.CLUB_ADMIN}>
            <ClubAdminDashboardLayout />
          </ProtectedRouteWithRole>
        }
      />

      {/* Super Admin Dashboard - Protected by Role */}
      <Route
        path="/dashboard/admin/*"
        element={
          <ProtectedRouteWithRole requiredRole={ROLES.SUPER_ADMIN}>
            <SuperAdminDashboardLayout />
          </ProtectedRouteWithRole>
        }
      />
    </Routes>
  );
};

// Super Admin Dashboard Layout Component
const SuperAdminDashboardLayout: React.FC = () => {
  return (
    <div className="dashboard-layout">
      <img
        src={img4}
        alt="background"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          objectPosition: 'center',
          filter: 'brightness(0.5)',
          opacity: 0.6,
          zIndex: 0,
          pointerEvents: 'none',
        }}
      />

      <div className="top-navbar">
        <div className="navbar-left-cluster">
          <Link to="/dashboard/admin" className="navbar-logo">
            NEXUS
          </Link>
        </div>
        <div className="nav-center">
          <Link to="/dashboard/admin/clubs" className="nav-link">
            CLUBS
          </Link>
          <Link to="/dashboard/admin/events" className="nav-link">
            EVENTS
          </Link>
        </div>

        {/* Promotion Notification Icon + Profile Section */}
        <div className="student-profile-nav" style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
          <PromotionNotificationIcon />
          <StudentProfileNavigation />
        </div>
      </div>

      <main className="dashboard-main" style={{ marginLeft: '0px' }}>
        <Routes>
          <Route path="/" element={<SuperAdminDashboard />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/clubs" element={<ClubsPage />} />
          <Route path="/clubs/:id" element={<ClubTemplate />} />
          <Route path="/events" element={<EventsPage />} />
          <Route path="/events-browse" element={<EventsPageNew />} />
          <Route path="/events/:eventId/edit" element={<EventEditPage />} />
          <Route path="/events/:eventId" element={<EventTemplate />} />
          <Route path="/promotions" element={<PromotionReview />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/invite-admins" element={<AdminInvitePortal />} />
          <Route path="/aboutus" element={<AboutUs />} />
          <Route path="/sponsors" element={<Sponsors />} />
        </Routes>
      </main>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </Router>
  );
};

export default App;
